import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Eye, ShieldCheck, X, Users } from "lucide-react";
import { toast } from "sonner";

// Watermarked dailies viewer.
// Opens the PH-provided files_url inside a sandboxed iframe with a
// translucent CSS overlay stamping the viewer's email + episode +
// timestamp on top. Every open logs a `dailies_views` row for the
// PH's audit trail.
//
// Note: we can't watermark someone else's Frame.io / Drive video at
// rest — this overlay is a strong deterrent (any screen-capture will
// include the watermark) without needing server-side re-encoding.
export default function DailiesViewer({ open, onOpenChange, projectId, episode }) {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !episode?.id) { setPayload(null); return; }
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.post(
          `/projects/${projectId}/episode-deliveries/${episode.id}/dailies-view`);
        setPayload(data);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Cannot open dailies");
        onOpenChange(false);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, episode?.id, projectId, onOpenChange]);

  if (!episode) return null;
  const wm = payload?.watermark;
  const url = payload?.files_url;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-black border border-white/10 text-white max-w-6xl p-0 overflow-hidden"
        data-testid="dailies-viewer-dialog">
        <DialogHeader className="p-4 pb-2">
          <DialogTitle className="font-display uppercase tracking-tight text-lg flex items-center gap-2">
            <Eye size={16} /> Watermarked Preview · Ep {episode.episode_number}
          </DialogTitle>
          <DialogDescription className="text-white/55 text-xs">
            Every open is audited and the viewer&apos;s email is stamped on top. Screen-share is auto-detectable.
          </DialogDescription>
        </DialogHeader>

        <div className="relative w-full aspect-video bg-black" data-testid="dailies-viewer-frame">
          {loading || !url ? (
            <div className="absolute inset-0 flex items-center justify-center text-white/55">
              Loading dailies…
            </div>
          ) : (
            <>
              {/* Sandboxed iframe — download-lock via sandbox attr */}
              <iframe
                src={url}
                sandbox="allow-same-origin allow-scripts allow-forms"
                referrerPolicy="no-referrer"
                className="absolute inset-0 w-full h-full border-0"
                title={`Ep ${episode.episode_number} dailies (watermarked)`}
                onContextMenu={(e) => e.preventDefault()}
                data-testid="dailies-iframe"
              />
              {/* Watermark overlay — 4 corners + centre tile.
                  Positioned OVER the iframe so any screen-capture picks
                  it up. `pointer-events-none` so it doesn't intercept
                  player controls inside the iframe. */}
              {wm && (
                <>
                  <WMStamp position="top-left" wm={wm} testid="dailies-wm-tl" />
                  <WMStamp position="top-right" wm={wm} testid="dailies-wm-tr" />
                  <WMStamp position="bottom-left" wm={wm} testid="dailies-wm-bl" />
                  <WMStamp position="bottom-right" wm={wm} testid="dailies-wm-br" />
                  {/* Faint centre stamp — largest, hardest to crop out */}
                  <div
                    className="absolute inset-0 flex items-center justify-center pointer-events-none select-none"
                    aria-hidden>
                    <div className="text-white/[0.06] font-mono text-xl md:text-3xl tracking-[0.4em] uppercase rotate-[-24deg]">
                      {wm.email || wm.name} · {wm.stamped_at?.slice(0, 16).replace("T", " ")}
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <div className="p-4 flex items-center justify-between border-t border-white/10">
          <div className="text-[10px] tracking-[0.22em] uppercase text-white/55 flex items-center gap-1.5">
            <ShieldCheck size={11} className="text-emerald-300" />
            Every open is audited · Do not screen-share
          </div>
          <Button variant="outline" onClick={() => onOpenChange(false)}
            className="h-9 px-4 border-white/15 text-white/85 hover:bg-white/5"
            data-testid="dailies-close-btn">
            <X size={12} className="mr-1.5" /> Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function WMStamp({ position, wm, testid }) {
  const posClass = {
    "top-left":     "top-3 left-4",
    "top-right":    "top-3 right-4 text-right",
    "bottom-left":  "bottom-3 left-4",
    "bottom-right": "bottom-3 right-4 text-right",
  }[position];
  return (
    <div
      className={`absolute ${posClass} pointer-events-none select-none`}
      data-testid={testid}
      aria-hidden
    >
      <div className="text-[10px] tracking-[0.2em] uppercase text-white/50 font-mono leading-tight">
        {wm.email || wm.name || "viewer"}
      </div>
      <div className="text-[9px] text-white/35 font-mono leading-tight mt-0.5">
        Ep {wm.episode} · {wm.stamped_at?.slice(11, 19)}
      </div>
    </div>
  );
}


// ─────────────────────────── AUDIT DIALOG ───────────────────────────
// PH-only view of every dailies open. Wired from an "Audit" pill on
// each episode row (rendered only for PH owners).
export function DailiesAuditDialog({ open, onOpenChange, projectId, episode }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open || !episode?.id) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(
          `/projects/${projectId}/episode-deliveries/${episode.id}/dailies-audit`);
        setRows(data.views || []);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Cannot read audit");
      } finally {
        setLoading(false);
      }
    })();
  }, [open, episode?.id, projectId]);

  if (!episode) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0c] border border-white/10 text-white max-w-xl"
        data-testid="dailies-audit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-lg flex items-center gap-2">
            <Users size={16} /> Dailies Audit · Ep {episode.episode_number}
          </DialogTitle>
          <DialogDescription className="text-white/55 text-xs">
            Every open of this episode&apos;s dailies. Only you (as PH) can see this trail.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2">
          {loading ? (
            <div className="text-sm text-white/45 py-6 text-center">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-white/45 py-6 text-center"
              data-testid="dailies-audit-empty">
              No one has opened these dailies yet.
            </div>
          ) : (
            <div className="space-y-2 max-h-[60vh] overflow-y-auto"
              data-testid="dailies-audit-list">
              {rows.map((v) => (
                <div key={v.id}
                  className="rounded-lg p-3 bg-white/[0.03] border border-white/8 flex items-center gap-3"
                  data-testid={`dailies-audit-row-${v.id}`}>
                  <div className="w-8 h-8 rounded-full bg-sky-500/15 text-sky-300 flex items-center justify-center font-semibold text-xs">
                    {(v.viewer_name || v.viewer_email || "?").slice(0, 1).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">
                      {v.viewer_name || v.viewer_email}
                    </div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-white/55">
                      {v.viewer_role?.replace(/_/g, " ") || "viewer"}
                    </div>
                  </div>
                  <div className="text-[10px] tracking-[0.18em] uppercase text-white/45 font-mono">
                    {v.viewed_at?.slice(0, 16).replace("T", " ")}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
